import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import '../../../assets/style/NewsUpdates.css';

const BASE_URL = 'https://mis.drtrefi.edu.ph';
const FALLBACK_IMAGE = '/placeholder-image.jpg';
const LIMIT = 12;

/* ---------------------------------- */
/* API CLIENT */
/* ---------------------------------- */
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

/* ---------------------------------- */
/* HELPERS */
/* ---------------------------------- */

// safer + flexible image handling
const buildImageUrl = (image) => {
  if (!image || image === 'null' || image === 'undefined') {
    return FALLBACK_IMAGE;
  }

  // already absolute
  if (/^https?:\/\//i.test(image)) return image;

  // filename only → assume articles folder
  if (!image.includes('/')) {
    return `${BASE_URL}/public/images/articles/${encodeURIComponent(image)}`;
  }

  // relative path
  const path = image.startsWith('/') ? image : `/${image}`;
  return `${BASE_URL}${encodeURI(path)}`;
};

const stripHtml = (html = '') =>
  html.replace(/<[^>]+>/g, '').trim();

const truncateWords = (text = '', limit = 10) => {
  const words = text.split(/\s+/);
  return words.length > limit
    ? `${words.slice(0, limit).join(' ')}...`
    : text;
};

const mapArticle = (article) => ({
  ...article,
  title: truncateWords(article.title, 5),
  body: truncateWords(stripHtml(article.body), 10),
  cover_image: buildImageUrl(article.cover_image),
});

/* ---------------------------------- */
/* COMPONENT */
/* ---------------------------------- */

const NewsArticleList = () => {
  const [articles, setArticles] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  /* ---------------------------------- */
  /* FETCH LOGIC */
  /* ---------------------------------- */

  const fetchArticles = useCallback(async (signal) => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);

    try {
      const { data } = await api.get('/api/articles', {
        params: {
          display_site: 'rtr',
          limit: LIMIT,
          offset,
        },
        signal,
      });

      const mapped = data?.results?.map(mapArticle) || [];

      setArticles((prev) => {
        const existingIds = new Set(prev.map((a) => a.id));
        const unique = mapped.filter((a) => !existingIds.has(a.id));
        return [...prev, ...unique];
      });

      if (mapped.length < LIMIT) {
        setHasMore(false);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Failed to fetch articles:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [offset, isLoading, hasMore]);

  useEffect(() => {
    const controller = new AbortController();
    fetchArticles(controller.signal);

    return () => controller.abort();
  }, [fetchArticles]);

  /* ---------------------------------- */
  /* HANDLERS */
  /* ---------------------------------- */

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      setOffset((prev) => prev + LIMIT);
    }
  };

  /* ---------------------------------- */
  /* RENDER */
  /* ---------------------------------- */

  return (
    <Container>
      <h1 className="my-4">News Articles</h1>

      <Row className="row-news">
        {!isLoading && articles.length === 0 && (
          <Col>
            <p>No news articles available.</p>
          </Col>
        )}

        {articles.map((article) => (
          <Col md={4} key={article.id} className="mb-4">
            <Card>
              <Card.Img
                variant="top"
                src={article.cover_image}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
              />

              <Card.Body>
                <Card.Title>{article.title}</Card.Title>

                <Card.Subtitle className="mb-2 text-muted">
                  {article.body}
                </Card.Subtitle>

                <Card.Text>{article.snippet}</Card.Text>

                <a
                  href={`/articles/singlepage/${article.short_title}`}
                  className="read-more"
                >
                  Read more
                </a>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="loadMore text-center my-4">
        {isLoading && <Spinner animation="border" />}

        {hasMore && (
          <Button
            variant="outline-success"
            onClick={handleLoadMore}
            disabled={isLoading}
          >
            LOAD MORE
          </Button>
        )}

        {!hasMore && !isLoading && (
          <p className="text-muted">No more articles.</p>
        )}
      </div>
    </Container>
  );
};

export default NewsArticleList;